/*
 * What a person can do with a photo in a chat, like in native messengers: save it, save it somewhere chosen
 * ("save as"), copy it to paste elsewhere, or share it (phones). Works for plain and encrypted photos (an
 * encrypted one is already decrypted on this device into a blob URL).
 */

/** Photos decrypted on this device, by message id: the message menu can then offer the same actions. */
const decryptedPhotos = new Map<string, { src: string; name: string }>();
export function rememberDecryptedPhoto(messageId: string, src: string, name: string): void {
  decryptedPhotos.set(messageId, { src, name });
}
export function decryptedPhoto(messageId: string): { src: string; name: string } | undefined {
  return decryptedPhotos.get(messageId);
}

/*
 * Decrypted files live at blob: addresses, which the page's security policy does not let it fetch back
 * (connect-src). So the decrypted file itself is kept here by its address and used directly.
 */
const blobs = new Map<string, Blob>();
export function keepBlob(url: string, blob: Blob): void {
  blobs.set(url, blob);
}

async function blobOf(src: string): Promise<Blob> {
  const kept = blobs.get(src);
  if (kept) return kept;
  const res = await fetch(src, { credentials: 'same-origin' });
  if (!res.ok) throw new Error(String(res.status));
  return res.blob();
}

/** Saves into the downloads folder. */
export async function saveImage(src: string, name: string): Promise<void> {
  const url = URL.createObjectURL(await blobOf(src));
  const a = document.createElement('a');
  a.href = url;
  a.download = name || 'photo';
  a.rel = 'noopener';
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 30_000);
}

type SavePicker = (opts: { suggestedName?: string; types?: { description?: string; accept: Record<string, string[]> }[] }) => Promise<{
  createWritable: () => Promise<{ write: (b: Blob) => Promise<void>; close: () => Promise<void> }>;
}>;

/** "Save as": the system's save dialog where the browser has one (Chrome, Edge), a plain save otherwise. */
export function canSaveAs(): boolean {
  return typeof (window as Window & { showSaveFilePicker?: SavePicker }).showSaveFilePicker === 'function';
}
export async function saveImageAs(src: string, name: string): Promise<void> {
  const picker = (window as Window & { showSaveFilePicker?: SavePicker }).showSaveFilePicker;
  const blob = await blobOf(src);
  if (!picker) return saveImage(src, name);
  const ext = /\.([a-z0-9]+)$/i.exec(name)?.[1]?.toLowerCase() ?? (blob.type.split('/')[1] || 'jpg');
  try {
    const handle = await picker({ suggestedName: name || `photo.${ext}`, types: [{ description: 'Image', accept: { [blob.type || 'image/jpeg']: [`.${ext}`] } }] });
    const w = await handle.createWritable();
    await w.write(blob);
    await w.close();
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') return;
    throw err;
  }
}

/** Copies the photo itself (as PNG, which every clipboard takes) to paste into another app. */
export function canCopyImage(): boolean {
  return typeof ClipboardItem !== 'undefined' && Boolean(navigator.clipboard?.write);
}
export async function copyImage(src: string): Promise<void> {
  // Safari wants the promise handed to ClipboardItem while still inside the click.
  const png = blobOf(src).then(async (blob) => {
    if (blob.type === 'image/png') return blob;
    const bitmap = await createImageBitmap(blob);
    const canvas = document.createElement('canvas');
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;
    canvas.getContext('2d')?.drawImage(bitmap, 0, 0);
    bitmap.close();
    return new Promise<Blob>((resolve, reject) => canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('encode'))), 'image/png'));
  });
  await navigator.clipboard.write([new ClipboardItem({ 'image/png': png })]);
}

/** Phones: the system share sheet (WhatsApp, Mail, Photos…). */
export function canShareFiles(): boolean {
  try {
    return typeof navigator.canShare === 'function' && navigator.canShare({ files: [new File([new Blob()], 'x.png', { type: 'image/png' })] });
  } catch {
    return false;
  }
}
export async function shareImage(src: string, name: string): Promise<void> {
  const blob = await blobOf(src);
  const file = new File([blob], name || 'photo.jpg', { type: blob.type || 'image/jpeg' });
  try {
    await navigator.share({ files: [file] });
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') return;
    throw err;
  }
}
